import { collectSiteComponents } from './commerce';
import {
  filterSlotsByDayRanges,
  isDaySlotActive,
  weekdayKeyFromDate,
} from './commerceAdmin';
import { resolveColorFillCss } from './colorFillUtils';

export function commerceDisplayColor(fill, fallback = '#2563eb') {
  if (!fill) return fallback;
  if (typeof fill === 'string') return fill;
  return resolveColorFillCss(fill, fallback) || fallback;
}

export const DEFAULT_MENU_THEME = {
  showOrnaments: true,
  showFrame: true,
  inkColor: '#432728',
  pageBackground: '#eae7e3',
};

export const DEFAULT_CLICK_COLLECT_DISPLAY = {
  pageTitle: '',
  pageSubtitle: 'vous propose',
  menuFooter: '',
  layout: 'classic',
  accentColor: '#92400e',
  backgroundColor: '#fffbf5',
  orderButtonColor: '#92400e',
  orderButtonTextColor: '#ffffff',
  menuTheme: { ...DEFAULT_MENU_THEME },
};

export const SUGGESTED_MENU_CATEGORIES = ['Entrées', 'Plats', 'Desserts', 'Boissons'];

export function normalizeMenuTheme(theme) {
  const t = theme && typeof theme === 'object' ? theme : {};
  return {
    showOrnaments: t.showOrnaments !== false,
    showFrame: t.showFrame !== false,
    inkColor: commerceDisplayColor(t.inkColor, DEFAULT_MENU_THEME.inkColor),
    pageBackground: commerceDisplayColor(t.pageBackground, DEFAULT_MENU_THEME.pageBackground),
  };
}

export function parseMenuFooterLines(menuFooter) {
  return String(menuFooter || '')
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export const DEFAULT_RESERVATION_DISPLAY = {
  formTitle: 'Réserver votre table',
  formSubtitle: 'Indiquez vos coordonnées et choisissez un créneau',
  submitButtonLabel: 'Envoyer ma demande',
  submitButtonLabelBusy: 'Envoi…',
  accentColor: '#0f766e',
  backgroundColor: '#f8fafc',
  buttonColor: '#0f766e',
  buttonTextColor: '#ffffff',
};

export function resolveReservationSubmitLabel(display, { busy, interactive, variant } = {}) {
  if (busy) {
    return String(display?.submitButtonLabelBusy || '').trim() || 'Envoi…';
  }
  if (!interactive) {
    const preview = String(display?.submitButtonLabelPreview || '').trim();
    if (preview) return preview;
  }
  const custom = String(display?.submitButtonLabel || '').trim();
  if (custom) return custom;
  if (variant === 'restaurant') return 'Demander ma réservation';
  return 'Envoyer ma demande';
}

function mergeDisplay(defaults, siteBlock, compBlock) {
  return {
    ...defaults,
    ...(siteBlock && typeof siteBlock === 'object' ? siteBlock : {}),
    ...(compBlock && typeof compBlock === 'object' ? compBlock : {}),
  };
}

export function resolveClickCollectDisplay(site) {
  const comp = collectSiteComponents(site).find((c) => c.type === 'clickAndCollect');
  const merged = mergeDisplay(
    DEFAULT_CLICK_COLLECT_DISPLAY,
    site?.clickAndCollect?.display,
    comp?.props?.menuPage,
  );
  const title =
    String(merged.pageTitle || '').trim() ||
    String(site?.title || '').trim() ||
    'Notre carte';
  return {
    ...merged,
    pageTitle: title,
    menuTheme: normalizeMenuTheme(merged.menuTheme),
  };
}

export function resolveReservationDisplay(site) {
  const comp = collectSiteComponents(site).find((c) => c.type === 'reservation');
  return mergeDisplay(
    DEFAULT_RESERVATION_DISPLAY,
    site?.reservation?.display,
    comp?.props?.reservationPage,
  );
}

export function groupMenuItemsByCategory(items) {
  const list = (items || []).filter(
    (i) => i.active !== false && (Number(i.quantityAvailable) || 0) > 0,
  );
  const map = new Map();
  for (const item of list) {
    const cat = String(item.category || '').trim() || 'La carte';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(item);
  }
  return Array.from(map.entries());
}

/** Créneaux horaires proposés (service midi / soir). */
export function buildReservationTimeSlots() {
  const slots = [];
  for (let h = 11; h <= 21; h += 1) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) break;
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
}

export function earliestReservationDate(leadTimeHours = 2) {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + Math.max(0, Number(leadTimeHours) || 0));
  if (d.getHours() < 11) {
    d.setHours(11, 0, 0, 0);
  }
  return d;
}

export function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatPreferredDateIso(date, timeSlot) {
  if (!date || !timeSlot) return '';
  const [hh, mm] = timeSlot.split(':').map((x) => Number(x) || 0);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, 0, 0);
  return d.toISOString();
}

export function dayKeyFromDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function remainingCoversOnDay(dayKey, coversPerDay, dayBookings = {}, partySize = 1) {
  const cap = Math.max(1, Number(coversPerDay) || 40);
  const booked = Number(dayBookings?.[dayKey]) || 0;
  return Math.max(0, cap - booked);
}

export function isDayFullyBooked(dayKey, coversPerDay, dayBookings, minParty = 1) {
  return remainingCoversOnDay(dayKey, coversPerDay, dayBookings, minParty) < minParty;
}

export function filterTimeSlotsForDate(slots, date, leadTimeHours, daySlot) {
  let list = Array.isArray(slots) ? [...slots] : [];
  if (daySlot) {
    if (!isDaySlotActive(daySlot)) return [];
    list = filterSlotsByDayRanges(list, daySlot);
  }
  if (!date) return list;
  const min = earliestReservationDate(leadTimeHours);
  if (!isSameCalendarDay(date, min)) return list;
  return list.filter((slot) => {
    const [hh, mm] = slot.split(':').map((x) => Number(x) || 0);
    const t = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm);
    return t >= min;
  });
}

/** Click & collect : créneaux du jour encore à venir (sans délai de préparation). */
export function filterPickupTimeSlotsForDate(slots, date, daySlot) {
  let list = Array.isArray(slots) ? [...slots] : [];
  if (daySlot) {
    if (!isDaySlotActive(daySlot)) return [];
    list = filterSlotsByDayRanges(list, daySlot);
  }
  if (!date) return list;
  const now = new Date();
  if (!isSameCalendarDay(date, now)) return list;
  return list.filter((slot) => {
    const [hh, mm] = slot.split(':').map((x) => Number(x) || 0);
    const t = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm);
    return t.getTime() > now.getTime();
  });
}

/** Premier jour / créneau de retrait disponible (click & collect). */
export function findEarliestPickupSlot(pickupWeek, _leadTimeHours = 1, maxDaysAhead = 14) {
  const slots = buildReservationTimeSlots();
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let offset = 0; offset <= maxDaysAhead; offset += 1) {
    const date = new Date(base);
    date.setDate(base.getDate() + offset);
    const daySlot = pickupWeek?.[weekdayKeyFromDate(date)];
    const available = filterPickupTimeSlotsForDate(slots, date, daySlot);
    if (available.length) {
      return { date, slot: available[0] };
    }
  }
  return null;
}
